// ─────────────────────────────────────────────────────────────
// 블로그 색인/누락 추이 기록 (Phase 63)
//
//   블로그 진단을 돌릴 때마다 그 결과를 브라우저 localStorage 에 스냅샷으로
//   누적 → 추이 그래프로 "누락이 줄어드는가"를 검증.
//   ⚠️ 서버에는 전혀 저장하지 않음 (개인 블로그 데이터 = 로컬에만).
//   같은 날 여러 번 진단하면 그 날의 마지막 값으로 덮어씀. 최근 90개 유지.
// ─────────────────────────────────────────────────────────────

export interface BlogSnapshot {
  date: string; // YYYY-MM-DD (로컬 기준)
  ts: number; // 기록 시각 epoch ms
  total: number; // 블로그 전체 글 수
  collected: number; // 분석 대상(최근 90일) 글 수
  checked: number; // 색인 검사 완료 수
  indexed: number; // 노출
  missing: number; // 누락
  indexRate: number; // 색인율 0~100
}

const MAX = 90;
const keyOf = (userId: string) => `blogTrend.${userId.trim().toLowerCase()}`;

function todayLocal(now = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** 저장된 추이 (오래된 → 최신 순). 손상된 값은 무시. */
export function getBlogTrend(userId: string): BlogSnapshot[] {
  if (typeof window === 'undefined' || !userId) return [];
  try {
    const raw = window.localStorage.getItem(keyOf(userId));
    if (!raw) return [];
    const arr = JSON.parse(raw) as unknown;
    if (!Array.isArray(arr)) return [];
    return arr
      .filter((s): s is BlogSnapshot =>
        !!s && typeof s === 'object' &&
        typeof (s as BlogSnapshot).date === 'string' &&
        typeof (s as BlogSnapshot).missing === 'number',
      )
      .sort((a, b) => a.ts - b.ts);
  } catch {
    return [];
  }
}

/** 진단 결과 1건 기록 → 갱신된 추이 반환. 같은 날은 덮어씀. */
export function recordBlogSnapshot(
  userId: string,
  snap: Omit<BlogSnapshot, 'date' | 'ts'>,
): BlogSnapshot[] {
  if (typeof window === 'undefined' || !userId) return [];
  const now = new Date();
  const entry: BlogSnapshot = { ...snap, date: todayLocal(now), ts: now.getTime() };
  const prev = getBlogTrend(userId).filter((s) => s.date !== entry.date);
  const next = [...prev, entry].sort((a, b) => a.ts - b.ts).slice(-MAX);
  try {
    window.localStorage.setItem(keyOf(userId), JSON.stringify(next));
  } catch {
    /* quota / disabled */
  }
  return next;
}

/** 추이 전체 삭제. */
export function clearBlogTrend(userId: string): void {
  if (typeof window === 'undefined' || !userId) return;
  try {
    window.localStorage.removeItem(keyOf(userId));
  } catch {
    /* */
  }
}
